import type { ComputedRef } from 'vue'
import type { AcquisitionKit, AcquisitionKitAvailable } from '~/utils/installation/Installation'
import { computed } from 'vue'
import { ACQUISITION_KIT_ORDER } from './equipment.constants'

export interface AcquisitionKitOption {
  label: string
  value: AcquisitionKit
  disabled: boolean
}

export interface UseAcquisitionKitOptionsReturn {
  options: ComputedRef<AcquisitionKitOption[]>
  firstEnabled: ComputedRef<AcquisitionKit | null>
}

/**
 * Builds the options for the single "Forma de aquisição do kit" toggle from the
 * `aquisition_kit_available` field of GET /hardware/form.
 *
 * Rules (driven by the backend):
 * - iterates in the official order (`ACQUISITION_KIT_ORDER`);
 * - includes only keys whose value is `!== null` (null = hidden);
 * - `disabled = value === false` (false = shown disabled, true = enabled);
 * - `firstEnabled` = first enabled option (true) in order — initial selection.
 *
 * Resilience: if `aquisition_kit_available` is missing/undefined/null, `options`
 * stays empty (the toggle is not rendered and the screen degrades safely).
 */
export function useAcquisitionKitOptions(
  getAvailable: () => AcquisitionKitAvailable | null | undefined,
  t: (key: string) => string,
): UseAcquisitionKitOptionsReturn {
  const options = computed<AcquisitionKitOption[]>(() => {
    const available = getAvailable()
    if (!available)
      return []

    return ACQUISITION_KIT_ORDER
      .filter(key => available[key] !== null && available[key] !== undefined)
      .map(key => ({
        label: t(`customer.equipment_step.acquisition.options.${key}`),
        value: key,
        disabled: available[key] === false,
      }))
  })

  const firstEnabled = computed<AcquisitionKit | null>(
    () => options.value.find(option => !option.disabled)?.value ?? null,
  )

  return { options, firstEnabled }
}
